import type { JSONContent } from "@tiptap/core";

const LEGACY_IMAGE_WIDTH_PATTERN = /^\{width:(\d+)\}/;

/** Drop `hardBreak`s stranded at the edges of a lifted inline run — the block
 * split itself now provides the line break (e.g. the `<br>` separating an icon
 * from its caption inside a `<div>` wrapper). */
const trimEdgeHardBreaks = (content: JSONContent[]): JSONContent[] => {
  let start = 0;
  let end = content.length;
  while (content[start]?.type === "hardBreak") {
    start += 1;
  }
  while (end > start && content[end - 1]?.type === "hardBreak") {
    end -= 1;
  }
  return content.slice(start, end);
};

/**
 * Repairs the legacy Markdown image-width suffix after it has been parsed to
 * Tiptap JSON. The old RawEditor writer produced `![](url){width:N}`; Tiptap's
 * Markdown parser correctly creates the image but leaves the suffix as text.
 */
export const normalizeLegacyImageContent = (
  content: JSONContent
): JSONContent => {
  const normalized = structuredClone(content);

  const liftImages = (node: JSONContent) => {
    if (!node.content) {
      return;
    }

    node.content.forEach(liftImages);
    const splitBlockChildren = (child: JSONContent): JSONContent[] => {
      if (
        (child.type !== "heading" && child.type !== "paragraph") ||
        !child.content?.some(
          (inline) => inline.type === "image" || inline.type === "paragraph"
        )
      ) {
        return [child];
      }

      const split: JSONContent[] = [];
      let inlineContent: JSONContent[] = [];
      const flushInlineContent = () => {
        inlineContent = trimEdgeHardBreaks(inlineContent);
        if (inlineContent.length > 0) {
          split.push({ ...child, content: inlineContent });
          inlineContent = [];
        }
      };

      for (let index = 0; index < child.content.length; index += 1) {
        const inline = child.content[index];
        // A paragraph nested inside a paragraph (inline HTML like a `<div>`
        // wrapper around an image and its caption) becomes a sibling block.
        if (inline.type === "paragraph") {
          flushInlineContent();
          split.push(
            ...splitBlockChildren({
              ...inline,
              content: trimEdgeHardBreaks(inline.content ?? []),
            })
          );
          continue;
        }
        if (inline.type !== "image") {
          inlineContent.push(inline);
          continue;
        }

        flushInlineContent();
        const marker = child.content[index + 1];
        const match =
          marker?.type === "text" && typeof marker.text === "string"
            ? LEGACY_IMAGE_WIDTH_PATTERN.exec(marker.text)
            : null;
        const width = match ? Number(match[1]) : NaN;
        split.push(
          Number.isSafeInteger(width) && width > 0
            ? { ...inline, attrs: { ...inline.attrs, width } }
            : inline
        );

        if (match && Number.isSafeInteger(width) && width > 0) {
          index += 1;
          const remainder = marker.text?.slice(match[0].length) ?? "";
          if (remainder) {
            inlineContent.push({ ...marker, text: remainder });
            continue;
          }
        }

        if (child.content[index + 1]?.type === "hardBreak") {
          index += 1;
        }
      }

      flushInlineContent();
      return split;
    };
    let blocks = node.content.flatMap(splitBlockChildren);

    if (node.type === "listItem" && blocks[0]?.type !== "paragraph") {
      blocks = [{ type: "paragraph" }, ...blocks];
    }

    node.content = blocks;
  };

  liftImages(normalized);
  return normalized;
};
